import React, {useContext, useEffect, useRef, useState} from "react";
import { Link } from "react-router-dom";

import {
    Table,
    Button,
    message,
    Tooltip,
    Tag,
    Avatar,
    Space,
    Popconfirm,
    Popover,
    Drawer,
    Modal,
    Card,
    List, Divider,
    Typography, Skeleton, Dropdown, Menu, Select, Descriptions, DatePicker, Input, Form
} from 'antd';

const { Text, Paragraph } = Typography;

import api from "../api";

import {
    AlertOutlined,
    ArrowRightOutlined, CheckCircleOutlined,
    DeleteOutlined, DownloadOutlined, InfoCircleOutlined, LoadingOutlined, SettingOutlined,
    UndoOutlined,
} from "@ant-design/icons";
import {Can} from "../helpers/Can";
import TimeAgo from "../helpers/TimeAgo";
import User from "../helpers/User";
import {MetaImportSetup} from "./MetaImports";
import useSetCurrentAsset from "../helpers/useSetCurrentAsset";
import AssetBrowseImage from "../explore/AssetBrowseImage";
import {useLoadedAssetsDispatch} from "../../contexts/LoadedAssetsContext";
import useShowAsset from "../helpers/useShowAsset";
import useCurrentOrg from "../helpers/useCurrentOrg";
import BulkJobProgress from "../explore/BulkJobProgress";
import {useTranslation} from "react-i18next";
import PaginationTotal from "../widgets/PaginationTotal";
import JobAssetsTable from "./JobAssetsTable";

const {RangePicker} = DatePicker;

export default ({q, onLoaded}) => {
    const {t} = useTranslation();
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10});
    const [loading, setLoading] = useState(false);
    const [detailsBj, setDetailsBj] = useState(null);
    const [undoProgressBj, setUndoProgressBj] = useState(null);
    const [assetsBj, setAssetsBj] = useState(null);

    const [filters, setFilters] = useState({user_id: null, dateRange: null, asset_guid: ''});
    const [memberOptions, setMemberOptions] = useState([]);
    const [memberSearchLoading, setMemberSearchLoading] = useState(false);
    const memberSearchTimer = useRef(null);

    const apiPath = '/api/bulk_jobs'

    const destroy = id => {
        console.log(id);
        api({
            url: `${apiPath}/${id}`,
            method: 'delete'
        }).then(res => {
            message.success(t('message-bulk-job-canceled.','Bulk Job Canceled.'));
            handleTableChange();
        });
    }

    const undoBulkJob = guid => {
        api.post(`${apiPath}/${guid}/undo`).then(res => {
            message.info(t('message-bulk-job-undo-started', 'Undo started.'));
            setUndoProgressBj(res.data);
            loadTableData();
        }).catch(err => {
            const status = err?.response?.status;
            if (status === 403) {
                message.error(t('message-bulk-job-undo-forbidden', 'You do not have permission to undo this bulk job.'));
            } else {
                message.error(t('message-bulk-job-undo-failed', 'Could not start undo.'));
            }
        });
    }

    const fullName = u => u && [u.first_name, u.last_name].filter(Boolean).join(' ') || u?.email;

    const renderStatusTag = (bj) => {
        const state = bj.aasm_state;
        let tag;
        if (state == 'processed') tag = <Tag color={'green'} icon={<CheckCircleOutlined/>}>{t('processed','Processed')}</Tag>
        else if (state == 'processing') tag = <Tag color={'blue'} icon={<LoadingOutlined/>}>{t('processing','Processing')}</Tag>
        else if (state == 'errored') tag = <Tag color={'red'} icon={<AlertOutlined/>}>{t('errored','Errored')}</Tag>
        else tag = <Tag>{t(state, state.toProperCase())}</Tag>;
        return <Tooltip title={<>ID: {bj.id}</>}>{tag}</Tooltip>;
    };

    const renderAssetCount = (count, bj) => {
        if (bj.asset_group_move) {
            return <Text>{t('library-items-count', '{{count}} item(s)', {count: bj.selected_asset_group_count || 0})}</Text>
        }

        return count > 0
            ? <Button type={'link'} onClick={() => setAssetsBj(bj)}>{count}</Button>
            : <Text type={'secondary'}>{count}</Text>
    }

    const columns = [
        {
            title: t('created','Created'),
            dataIndex: 'created_at',
            sorter: true,
            render: date => <TimeAgo date={date}/>
        },
        // {
        //     title: 'Processed In',
        //     render: bj => <>{}</>
        // },
        {
            title: t('user','User'),
            render: (m) => {
                return <User user={m.user}/>
            }
        },
        {
            title: t('summary','Summary'),
            key: 'summary',
            render: (bj) => {
                const more = (bj.operations?.length || 0) - 1;
                return (
                    <Space size={4}>
                        <span>
                            {bj.summary || bj.action}
                            {more > 0 && <Text type={'secondary'}> {t('plus-n-more', '+{{n}} more', {n: more})}</Text>}
                        </span>
                        <Tooltip title={t('view-bulk-job-details', 'View details')}>
                            <Button type={'text'} size={'small'} icon={<InfoCircleOutlined/>} onClick={() => setDetailsBj(bj)}/>
                        </Tooltip>
                    </Space>
                );
            }
        },
        {
            title: t('status','Status'),
            dataIndex: 'aasm_state',
            sorter: true,
            render: (_, bj) => renderStatusTag(bj),
        },
        {
            title: t('Assets','Assets'),
            dataIndex: 'progress_total_count',
            sorter: true,
            render: renderAssetCount
        },
        {
            title: t('progress','Progress'),
            render: (bj) => {
                if(bj.canceled_at) return <></>

                return (
                    <>
                        {!bj.finished_processing_at ? (
                            <BulkJobProgress id={bj.guid} onFinish={handleTableChange} onError={handleTableChange} onCancel={loadTableData}/>
                        ) : bj.asset_group_move && bj.indexing_pending ? (
                            <Space direction={'vertical'} size={0}>
                                <span>100%</span>
                                <Text type={'secondary'}>{t('search-catching-up', 'Search catching up')}</Text>
                            </Space>
                        ) : (
                            <>100%</>
                        )}
                    </>
                )
            }
        },
        {
            title: t('undo','Undo'),
            key: 'undo',
            render: (bj) => {
                if (bj.undone_at) {
                    return (
                        <Tooltip title={fullName(bj.undone_by) || t('unknown-user', 'unknown user')}>
                            <Text type={'secondary'}>
                                {t('undone-by', 'undone by')} {fullName(bj.undone_by) || '—'} <TimeAgo date={bj.undone_at}/>
                            </Text>
                        </Tooltip>
                    );
                }

                if (bj.aasm_state !== 'processed') return null;
                if (bj.undo_bulk_job_id) return <Text type={'secondary'}>{t('is-an-undo','(this is an undo)')}</Text>;

                const ageDays = (Date.now() - new Date(bj.created_at).getTime()) / 86400000;
                const tooOld = ageDays > 30;

                return (
                    <Popconfirm
                        title={tooOld
                          ? t('undo-too-old', 'This bulk job is older than 30 days and cannot be undone.')
                          : t('confirm-undo-bulk-job', 'Undo this bulk job? Fields you have changed since will be preserved.')
                        }
                        onConfirm={() => undoBulkJob(bj.guid)}
                        okText={t('undo','Undo')}
                        disabled={tooOld}
                        okButtonProps={{disabled: tooOld}}
                    >
                        <Button size={'small'} icon={<UndoOutlined/>} disabled={tooOld}>
                            {t('undo','Undo')}
                        </Button>
                    </Popconfirm>
                );
            }
        },
        // {
        //     key: 'action',
        //     width: 100,
        //     render: (bj) => {
        //         return (
        //             <>
        //                 <Space size={5}>
        //                     <Can I={'edit'} a={'BulkJob'}>
        //                         <Space direction={'horizontal'}>
        //                             {(bj.aasm_state != 'processing' && bj.aasm_state != 'processed') && (
        //                                 <Popconfirm
        //                                     title={t('confirm-cancel-bulk-job','Cancel Bulk Job?')}
        //                                     onConfirm={() => destroy(bj.id)}
        //                                 >
        //                                     <Button size={'middle'}>
        //                                         <DeleteOutlined/>
        //                                     </Button>
        //                                 </Popconfirm>
        //                             )}
        //                         </Space>
        //                     </Can>
        //                 </Space>
        //             </>
        //         );
        //     }
        // }
    ];

    const params = useRef({})

    const handleTableChange = (pagination, antdFilters, sorter) => {
        params.current = {
            q,
            sortField: sorter?.field,
            sortOrder: sorter?.order,
            pagination,
            ...antdFilters,
        }
        loadTableData();
    }

    const loadTableData = ()=>{
        setLoading(true);
        const apiParams = {
            ...params.current,
            user_id: filters.user_id || undefined,
            asset_guid: filters.asset_guid?.trim() || undefined,
            created_after: filters.dateRange?.[0]?.toISOString() || undefined,
            created_before: filters.dateRange?.[1]?.toISOString() || undefined,
        };
        api.get(apiPath, { params: apiParams }).then(res => {
            setData(res.data)
            setPagination({
                ...params.current.pagination,
                total: res.headers['total-entries']
            })
            setLoading(false)
            onLoaded && onLoaded()
        })
    }

    useEffect(handleTableChange, [q]);
    useEffect(() => { loadTableData(); }, [filters.user_id, filters.dateRange, filters.asset_guid]);

    const searchMembers = (text) => {
        if (memberSearchTimer.current) clearTimeout(memberSearchTimer.current);
        memberSearchTimer.current = setTimeout(() => {
            setMemberSearchLoading(true);
            api.get('/api/memberships/search', { params: { q: text } }).then(res => {
                const opts = (res.data || []).map(m => ({
                    value: m.user?.id,
                    label: [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ') || m.user?.email || m.username,
                })).filter(o => o.value);
                setMemberOptions(opts);
                setMemberSearchLoading(false);
            }).catch(() => setMemberSearchLoading(false));
        }, 250);
    };

    return (
        <>
            <Space wrap style={{marginBottom: 12}}>
                <RangePicker
                    showTime={{format: 'HH:mm'}}
                    value={filters.dateRange}
                    onChange={(range) => setFilters(f => ({...f, dateRange: range}))}
                    placeholder={[t('from','From'), t('to','To')]}
                />
                <Select
                    showSearch
                    allowClear
                    placeholder={t('filter-by-user','Filter by user')}
                    value={filters.user_id}
                    onChange={(uid) => setFilters(f => ({...f, user_id: uid}))}
                    onSearch={searchMembers}
                    onFocus={() => memberOptions.length === 0 && searchMembers('')}
                    filterOption={false}
                    loading={memberSearchLoading}
                    options={memberOptions}
                    style={{minWidth: 220}}
                    notFoundContent={memberSearchLoading ? t('searching','Searching…') : t('no-results','No results')}
                />
                <Input.Search
                    placeholder={t('search-by-asset-guid','Asset GUID')}
                    allowClear
                    onSearch={(val) => setFilters(f => ({...f, asset_guid: val}))}
                    style={{width: 320}}
                />
                {(filters.dateRange || filters.user_id || filters.asset_guid) && (
                    <Button onClick={() => setFilters({user_id: null, dateRange: null, asset_guid: ''})}>
                        {t('clear-filters','Clear filters')}
                    </Button>
                )}
            </Space>

            <Table
                bordered
                scroll={{x: true}}
                size={'small'}
                columns={columns}
                rowKey={record => record.id}
                dataSource={data}
                pagination={pagination}
                loading={loading}
                onChange={handleTableChange}
                footer={()=>(
                    <div style={{display:'flex', alignItems:'center'}}>
                        <PaginationTotal pagination={pagination}/>
                    </div>
                )}
            />

            <Modal
                open={!!detailsBj}
                title={detailsBj ? t('bulk-job-details', 'Bulk Job #{{id}} — what it did', {id: detailsBj.id}) : null}
                onCancel={() => setDetailsBj(null)}
                footer={null}
                width={640}
            >
                {detailsBj && (
                    <Descriptions bordered column={1} size={'small'}>
                        <Descriptions.Item label={t('user','User')}><User user={detailsBj.user}/></Descriptions.Item>
                        <Descriptions.Item label={t('created','Created')}><TimeAgo date={detailsBj.created_at}/></Descriptions.Item>
                        <Descriptions.Item label={detailsBj.asset_group_move ? t('library-items', 'Library Items') : t('Assets','Assets')}>
                            {detailsBj.asset_group_move ? detailsBj.selected_asset_group_count : detailsBj.total_assets}
                        </Descriptions.Item>
                        {detailsBj.asset_group_move && (
                            <Descriptions.Item label={t('search-status', 'Search Status')}>
                                {detailsBj.indexing_status}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label={t('status','Status')}>{renderStatusTag(detailsBj)}</Descriptions.Item>
                        <Descriptions.Item label={t('operations','Operations')}>
                            <List
                                size={'small'}
                                dataSource={detailsBj.operations || [detailsBj.action]}
                                renderItem={(op) => <List.Item><Text code>{op}</Text></List.Item>}
                            />
                        </Descriptions.Item>
                        {detailsBj.undone_at && (
                            <Descriptions.Item label={t('undone','Undone')}>
                                {t('undone-by','undone by')} {fullName(detailsBj.undone_by) || '—'} <TimeAgo date={detailsBj.undone_at}/>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                )}
            </Modal>

            <Modal
                open={!!undoProgressBj}
                title={t('undo-in-progress','Undo in progress')}
                onCancel={() => { setUndoProgressBj(null); loadTableData(); }}
                footer={null}
                width={520}
                destroyOnClose
            >
                {undoProgressBj && (
                    <BulkJobProgress
                        id={undoProgressBj.guid}
                        onFinish={loadTableData}
                        onError={loadTableData}
                    />
                )}
            </Modal>

            <Modal
                open={!!assetsBj}
                title={assetsBj ? t('bulk-job-assets', 'Bulk Job #{{id}} — assets', {id: assetsBj.id}) : null}
                onCancel={() => setAssetsBj(null)}
                footer={null}
                width={'66vw'}
                destroyOnClose
            >
                {assetsBj && (
                    <JobAssetsTable apiPath={`/api/bulk_jobs/${assetsBj.guid}/assets`}/>
                )}
            </Modal>
        </>
    );
}
